/**
 * BRIDGES registry — every lifecycle bridge (IHP⇄504⇄IEP), each leg AND its inverse.
 * Loads schema/bridges.json (single canonical source per ADR-0017), Zod-validates
 * at module load, FAIL CLOSED. Lifecycle is BIDIRECTIONAL (CP-R1317): a leg whose
 * inverse is missing, or whose inverse does not point back, refuses to load.
 */
import { z } from 'zod'
import { AppError } from '../errors/app-error'
import bridgesJson from '../../../../schema/bridges.json'

export const BridgeCarryTypeSchema = z.enum(['accommodation', 'modification', 'nursing'])
export type BridgeCarryType = z.infer<typeof BridgeCarryTypeSchema>

export const BridgeLegSchema = z
  .object({
    id: z.string().min(1),
    fn: z.string().min(1),
    inverse: z.string().min(1),
    allow: z.array(BridgeCarryTypeSchema).min(1),
    note: z.string().min(1),
  })
  .strict()

export type BridgeLeg = z.infer<typeof BridgeLegSchema>

const BridgesFileSchema = z
  .object({
    description: z.string(),
    version: z.string(),
    target: z.string(),
    roadmapNote: z.string(),
    bridges: z.array(BridgeLegSchema).min(2),
    allowedTypes: z.record(z.string()),
    filters: z.record(z.object({ description: z.string() }).strict()),
  })
  .strict()

function loadBridges(): readonly BridgeLeg[] {
  const parsed = BridgesFileSchema.safeParse(bridgesJson)
  if (!parsed.success) {
    throw AppError.validation(
      'BRIDGES registry failed validation — refusing to load a partial or malformed bridge registry (fail closed)',
      parsed.error.issues,
    )
  }
  const legs = parsed.data.bridges
  const byId = new Map<string, BridgeLeg>()
  for (const leg of legs) {
    if (byId.has(leg.id)) {
      throw AppError.validation(`BRIDGES registry: duplicate bridge id '${leg.id}'`)
    }
    byId.set(leg.id, leg)
  }
  const declaredTypes = new Set(Object.keys(parsed.data.allowedTypes))
  for (const leg of legs) {
    const inverse = byId.get(leg.inverse)
    if (!inverse) {
      throw AppError.validation(
        `BRIDGES registry: leg '${leg.id}' names inverse '${leg.inverse}' which does not exist — every bridge needs its RETURN LEG (lifecycle is bidirectional)`,
      )
    }
    if (inverse.inverse !== leg.id) {
      throw AppError.validation(
        `BRIDGES registry: leg '${leg.id}' → inverse '${leg.inverse}' does not point back (it points to '${inverse.inverse}')`,
      )
    }
    for (const t of leg.allow) {
      if (!declaredTypes.has(t)) {
        throw AppError.validation(`BRIDGES registry: leg '${leg.id}' allows undeclared carry type '${t}'`)
      }
    }
  }
  return Object.freeze(legs)
}

/** Every bridge leg, validated (each with a verified inverse). Frozen. */
export const BRIDGES: readonly BridgeLeg[] = loadBridges()

const BRIDGE_INDEX: ReadonlyMap<string, BridgeLeg> = new Map(BRIDGES.map((b) => [b.id, b]))

export function hasBridge(id: string): boolean {
  return BRIDGE_INDEX.has(id)
}

/** Resolve a bridge leg by id. THROWS on unknown id — no orphaned one-way bridges. */
export function getBridge(id: string): BridgeLeg {
  const b = BRIDGE_INDEX.get(id)
  if (!b) {
    throw AppError.notFound(`Unknown bridge id '${id}' — bridges resolve from the BRIDGES registry only`)
  }
  return b
}

/** The return leg of a bridge. Guaranteed to exist by load-time validation. */
export function inverseBridge(id: string): BridgeLeg {
  return getBridge(getBridge(id).inverse)
}
